import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import {
  Box,
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import {
  ArrowDropDown,
  ArrowDropUp,
  Code,
  ContactSupport,
  ExpandLess,
  ExpandMore,
  Launch,
  BookRounded,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useLocation, matchPath } from 'react-router-dom';
import classNames from 'classnames';

import {
  DOCUMENT_LINK,
  LANDING_PAGE_URL,
  VBEE_CONSOLE_URL,
} from '@src/configs';
import actions from '@src/redux/actions';

import ROUTES from '@src/constants/route';
import { getCollapseInitialState } from '@src/services/sidebar';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { useCheckProductName } from '@src/hooks/useCheckProduct';
import {
  checkVoucherByProductForHeaderBanner,
  verifyTime,
} from '@src/services/voucher';
import apis from '@src/apis';
import {
  useCheckDubbingRoute,
  useCheckTTSRoute,
} from '@src/hooks/useCheckDubbingRoute';
import { dubbingAppRoutes, ttsAppRoutes } from '@src/router/newAppRoutes';
import { MOBILE_BREAKPOINT } from '@src/constants';
import {
  StyledBetaWrapper,
  StyledSidebarTooltip,
  StyledSubMenu,
} from './index.style';
import MobileSidebar from './MobileSidebar';
import DesktopSidebar from './DesktopSidebar';

const NEW_SUPPORT_MENU = [
  {
    label: 'userGuide',
    href: DOCUMENT_LINK,
    isActive: true,
  },
  {
    label: 'supportPage',
    href: `${LANDING_PAGE_URL}/support`,
    isActive: true,
  },
];

const Sidebar = ({
  openSidebar,
  handleOpenSidebar,
  handleCloseSidebar,
  ipLocation,
  useHeaderBanner,
  headerVoucher,
}) => {
  const sidebarBodyRef = useRef(null);
  const [collapseState, setCollapseState] = useState({});
  const [openSubMenu, setOpenSubMenu] = useState(false);
  const [showScrollbar, setShowScrollbar] = useState(false);
  const { getFeatureValue } = useFeatureFlags();

  // let scrollTimeout = null;

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const { lastExpiredOrder } = useSelector((state) => state.user);
  const { lockCharacters } = useSelector((state) => state.auth.user);

  const product = useCheckProductName();

  const checkCharacterReservationPeriod = () => {
    let preservedCharacterDate;

    preservedCharacterDate = user.preservationExpiryDate;
    if (user.preservationExpiryDate)
      preservedCharacterDate = moment(user.preservationExpiryDate);
    else if (user.packageExpiryDate) {
      preservedCharacterDate = moment(user.packageExpiryDate).add(30, 'days');
    } else if (lastExpiredOrder?.id) {
      const expiryIn =
        lastExpiredOrder.customPackage?.expiresIn ||
        lastExpiredOrder.package?.expiresIn;
      preservedCharacterDate = moment(lastExpiredOrder.confirmedAt)
        .add(expiryIn, 'days')
        .add(30, 'days');
    }

    if (!preservedCharacterDate) return false;
    return moment().isBefore(moment(preservedCharacterDate));
  };

  const hasLockCharacters =
    useCheckTTSRoute() &&
    lockCharacters > 0 &&
    checkCharacterReservationPeriod();

  const activeRoute = (routePath) =>
    matchPath(location.pathname, { path: routePath, exact: true });

  const isDubbingRoute = useCheckDubbingRoute();
  const appRoutes = isDubbingRoute ? dubbingAppRoutes : ttsAppRoutes;
  let sidebarRoute = appRoutes.filter((route) => route.sidebar);

  sidebarRoute = sidebarRoute.filter((route) => route.path !== ROUTES.DUBBING);

  sidebarRoute = [
    ...sidebarRoute,
    {
      name: 'userGuide',
      path: DOCUMENT_LINK,
      subPaths: [],
      component: null,
      icon: BookRounded,
      exact: true,
      restricted: false,
      isPrivate: true,
      sidebar: true,
      externalUrl: true,
    },
  ];

  // const handleScroll = () => {
  //   setShowScrollbar(true);

  //   clearTimeout(scrollTimeout);

  //   scrollTimeout = setTimeout(() => {
  //     setShowScrollbar(false);
  //   }, 2000);
  // };

  // useEffect(() => () => clearTimeout(scrollTimeout), [showScrollbar]);

  const createLinks = (aRoutes, props) =>
    aRoutes.map((route) => {
      const hasEnabledRoute = route.featureKey
        ? getFeatureValue(route.featureKey, {
            userId: user?.id,
            email: user?.email,
            phoneNumber: user?.phoneNumber,
          })
        : true;
      if (!hasEnabledRoute) return null;

      const isActiveRoute =
        activeRoute(route.path) ||
        route.subPaths?.some((path) => activeRoute(path));

      if (route.collapse) {
        const collapse = {};
        collapse[route.name] = !collapseState[route.name];

        return (
          <ListItem
            key={route.name}
            className="item"
            onMouseEnter={() => setShowScrollbar(true)}
            onMouseLeave={() => setShowScrollbar(false)}
          >
            <NavLink
              to="#"
              className={classNames('item-link', {
                'collapse-active': getCollapseInitialState(route.items),
              })}
              onClick={(e) => {
                e.preventDefault();
                setCollapseState(collapse);
              }}
            >
              {openSidebar ? (
                <>
                  <route.icon
                    className={classNames('item-icon', {
                      'item-icon-active': isActiveRoute,
                    })}
                  />
                  <ListItemText
                    primary={t(route.name)}
                    secondary={
                      collapseState[route.name] ? (
                        <ArrowDropUp className="caret" />
                      ) : (
                        <ArrowDropDown className="caret" />
                      )
                    }
                    disableTypography
                    className={classNames('item-text', {
                      'item-text-active': isActiveRoute,
                    })}
                  />
                </>
              ) : (
                <StyledSidebarTooltip title={t(route.name)}>
                  <route.icon className="item-icon" />
                </StyledSidebarTooltip>
              )}
            </NavLink>
            <Collapse in={collapseState[route.state]} unmountOnExit>
              <List className="list collapse-list">
                {createLinks(route.items)}
              </List>
            </Collapse>
          </ListItem>
        );
      }

      if (route?.externalUrl) {
        return (
          <ListItem
            key={route.name}
            className="item"
            onMouseEnter={() => setShowScrollbar(true)}
            onMouseLeave={() => setShowScrollbar(false)}
          >
            <a
              href={route.path}
              target="_blank"
              rel="noopener noreferrer"
              className={classNames('item-link', {
                'active-link': isActiveRoute,
              })}
              onClick={() => {
                if (props?.mobile) handleCloseSidebar();
              }}
            >
              {openSidebar ? (
                <>
                  <route.icon
                    className={classNames('item-icon', {
                      'item-icon-active': isActiveRoute,
                    })}
                  />
                  <div className="item-wrapper">
                    <ListItemText
                      primary={t(route.name)}
                      disableTypography
                      className={classNames('item-text', {
                        'item-text-active': isActiveRoute,
                      })}
                    />
                    {route.beta && (
                      <StyledBetaWrapper active={isActiveRoute}>
                        <div className="beta-background" />
                        <Typography className="beta-text">Beta</Typography>
                      </StyledBetaWrapper>
                    )}
                    {route.isNewFeature && (
                      <StyledBetaWrapper active={isActiveRoute}>
                        <div className="beta-background" />
                        <Typography className="beta-text">New</Typography>
                      </StyledBetaWrapper>
                    )}
                  </div>
                </>
              ) : (
                <StyledSidebarTooltip title={t(route.name)}>
                  <route.icon className="item-icon" />
                </StyledSidebarTooltip>
              )}
            </a>
          </ListItem>
        );
      }

      return (
        <ListItem
          key={route.name}
          className="item"
          onMouseEnter={() => setShowScrollbar(true)}
          onMouseLeave={() => setShowScrollbar(false)}
        >
          <NavLink
            to={route.path}
            className={classNames('item-link', {
              'active-link': isActiveRoute,
            })}
            onClick={() => {
              if (props?.mobile) handleCloseSidebar();
            }}
          >
            {openSidebar ? (
              <>
                <route.icon
                  className={classNames('item-icon', {
                    'item-icon-active': isActiveRoute,
                  })}
                />
                <div className="item-wrapper">
                  <ListItemText
                    primary={t(route.name)}
                    disableTypography
                    className={classNames('item-text', {
                      'item-text-active': isActiveRoute,
                    })}
                  />
                  {route.beta && (
                    <StyledBetaWrapper active={isActiveRoute}>
                      <div className="beta-background" />
                      <Typography className="beta-text">Beta</Typography>
                    </StyledBetaWrapper>
                  )}
                  {route.isNewFeature && (
                    <StyledBetaWrapper active={isActiveRoute}>
                      <Typography
                        className="new-text"
                        sx={{ textTransform: 'none !important' }}
                      >
                        {t('new')}
                      </Typography>
                    </StyledBetaWrapper>
                  )}
                </div>
              </>
            ) : (
              <StyledSidebarTooltip title={t(route.name)}>
                <route.icon className="item-icon" />
              </StyledSidebarTooltip>
            )}
          </NavLink>
        </ListItem>
      );
    });

  const renderSubMenuSupport = ({ props, className, itemClassName }) => (
    <StyledSubMenu className={className}>
      <List component="div" disablePadding>
        {NEW_SUPPORT_MENU.filter((item) => item.isActive).map((item) => (
          <a
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
            className={`item-link ${itemClassName}`}
            onClick={() => {
              if (props?.mobile) handleCloseSidebar();
            }}
          >
            <Typography className="item-text">{t(item.label)}</Typography>
          </a>
        ))}
      </List>
    </StyledSubMenu>
  );

  useEffect(() => {
    if (openSubMenu) {
      // Scroll to bottom when open sub menu on small screen
      // Delay to scroll to bottom when open sub menu on small screen to wait for the sub menu to be rendered
      setTimeout(() => {
        const sidebarBody = sidebarBodyRef.current;
        sidebarBody.lastElementChild.scrollIntoView({
          behavior: 'smooth',
        });
      }, 200);
    }
  }, [openSubMenu]);

  const renderSideBarItems = (props) => (
    <div
      ref={sidebarBodyRef}
      onMouseEnter={() => setShowScrollbar(true)}
      onMouseLeave={() => setShowScrollbar(false)}
      className={`sidebar-wrapper ${
        showScrollbar ? 'small-scrollbar' : 'hidden-scrollbar'
      } `}
      // onScroll={handleScroll}
    >
      <List className="list">{createLinks(sidebarRoute, props)}</List>
      <Divider />
      <Box marginY={1}>
        <Box
          className="item-link"
          onClick={() => setOpenSubMenu((prev) => !prev)}
        >
          <ContactSupport className="item-icon" />
          <Box className="item-wrapper">
            <Typography className="item-text">{t('support')}</Typography>
            {openSubMenu ? (
              <ExpandLess className="item-icon" />
            ) : (
              <ExpandMore className="item-icon" />
            )}
          </Box>
        </Box>
      </Box>

      <Collapse in={openSubMenu} timeout="auto" unmountOnExit>
        {renderSubMenuSupport({ props, itemClassName: 'sub-menu-item' })}
      </Collapse>

      <Divider />
      <a
        href={VBEE_CONSOLE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="item-link"
        onClick={() => {
          if (props?.mobile) handleCloseSidebar();
        }}
      >
        <Code className="item-icon" />
        <div className="item-wrapper">
          <Typography className="item-text">{t('api')}</Typography>
          <Launch className="item-icon" />
        </div>
      </a>
    </div>
  );

  const getSatisfiedVouchers = (
    voucherData,
    isUseHeaderBanner,
    isUseVoucherByProduct,
  ) => {
    const newVouchers = voucherData.reduce((acc, currVoucher) => {
      const satisfiedTime = verifyTime(currVoucher);
      if (!satisfiedTime) return acc;

      const canUse =
        satisfiedTime &&
        currVoucher?.remainingAmount > 0 &&
        !currVoucher.isPrivate &&
        currVoucher.usedAmount < currVoucher.numReuse;
      return [...acc, { ...currVoucher, satisfiedTime, canUse }];
    }, []);

    const sortVouchers = newVouchers.sort((voucher) =>
      voucher.canUse ? -1 : 1,
    );

    // this below code is the logic to check dispatch headerVoucher or not
    // if feature flag flash-sale is off => always dispatch undefined
    if (isUseVoucherByProduct && sortVouchers.length > 0) {
      const canUseVoucher = sortVouchers.filter(
        (voucher) =>
          voucher.canUse &&
          checkVoucherByProductForHeaderBanner({ voucher, product }),
      );
      const checkVoucherForLockCharacterInListVouchers = canUseVoucher.find(
        (voucher) => voucher.isPreserveVoucher,
      );
      if (hasLockCharacters && !checkVoucherForLockCharacterInListVouchers) {
        canUseVoucher.push({
          canUse: true,
          isPreserveVoucher: true,
        });
      }
      const checkMobile = window.innerWidth <= MOBILE_BREAKPOINT;
      let finalHeaderVoucher = [];
      if (!checkMobile) {
        finalHeaderVoucher = canUseVoucher.filter(
          (item) => item.isHeaderBanner,
        );
      } else {
        finalHeaderVoucher = [...canUseVoucher];
      }
      if (finalHeaderVoucher.length > 0 && isUseHeaderBanner) {
        dispatch(actions.voucher.updateHeaderVoucher(finalHeaderVoucher));
      } else {
        dispatch(actions.voucher.updateHeaderVoucher(undefined));
      }
    } else if (!isUseVoucherByProduct && sortVouchers.length > 0) {
      const flashSaleVoucher = sortVouchers.filter(
        (voucher) => voucher.isHeaderBanner && voucher.canUse,
      );
      if (
        flashSaleVoucher &&
        flashSaleVoucher.length > 0 &&
        isUseHeaderBanner
      ) {
        dispatch(actions.voucher.updateHeaderVoucher([flashSaleVoucher[0]]));
      } else {
        dispatch(actions.voucher.updateHeaderVoucher(undefined));
      }
    } else {
      dispatch(actions.voucher.updateHeaderVoucher(undefined));
    }
  };

  const fetchVouchers = async (isUseHeaderBanner, isUseVoucherByProduct) => {
    const data = await apis.vouchers.getVouchers();
    if (data.status)
      getSatisfiedVouchers(
        data.result,
        isUseHeaderBanner,
        isUseVoucherByProduct,
      );
  };

  useEffect(() => {
    fetchVouchers(useHeaderBanner, true);
  }, [product, useHeaderBanner]);

  return (
    <>
      <DesktopSidebar
        openSidebar={openSidebar}
        handleOpenSidebar={handleOpenSidebar}
        handleCloseSidebar={handleCloseSidebar}
        ipLocation={ipLocation}
        useHeaderBanner={useHeaderBanner}
        headerVoucher={headerVoucher}
      />

      <MobileSidebar
        openSidebar={openSidebar}
        handleCloseSidebar={handleCloseSidebar}
        useHeaderBanner={useHeaderBanner}
        headerVoucher={headerVoucher}
        renderSideBarItems={renderSideBarItems}
        createLinks={createLinks}
      />
    </>
  );
};

export default Sidebar;
